import { useToggle } from 'ahooks'
import { Button, List, Modal, Spin, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { useGetViewerFinishedEventsQuery, useGetViewerUnStartEventsQuery } from '@/generated/graphql'
import { EventActions } from '@/modules/calendar/event-card/event-card-actions.component'
import { routes } from '@/routes'

import s from './my-event-modal.module.less'

export const MyEventModal: React.FC = ({ children, ...props }) => {
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()
  children = React.cloneElement(children as any, {
    ...props,
    onClick: toggle,
  })

  return (
    <>
      {children}
      <Modal
        width={600}
        bodyStyle={{ padding: 0 }}
        visible={visible}
        title={t('viewer.event.title')}
        onCancel={() => toggle()}
        onOk={() => toggle()}
        closable={false}
        destroyOnClose
      >
        <MyUnStartEvent />
        <MyFinishedEvent />
      </Modal>
    </>
  )
}

export const MyUnStartEvent: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const format = 'MMMM Do (dddd) ⋅ a h:mm'
  const pagination = {
    first: 3,
    sortBy: 'startTime',
  }

  const { data, loading, fetchMore, refetch } = useGetViewerUnStartEventsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      pagination,
    },
  })

  const events = data?.viewer.unStartEvents?.edges.map(({ node }) => node) || []

  React.useEffect(() => {
    if (events.length > 0 && events.some((event) => !event.isParticipated && !event.isInterested)) {
      refetch()
    }
  }, [events])

  const pageInfo = data?.viewer.unStartEvents?.pageInfo || {
    startCursor: null,
    endCursor: null,
    hasNextPage: null,
    hasPreviousPage: null,
  }

  const loadingComponent = (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Spin siz='large' delay={300}></Spin>
    </div>
  )

  const header = (
    <Typography.Title className={s.header} level={5} style={{ margin: '16px 0' }}>
      {t('viewer.event.unStart')}
    </Typography.Title>
  )

  const loadMore = pageInfo.hasNextPage && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
      <Button
        shape='round'
        onClick={() =>
          fetchMore({
            variables: {
              pagination: {
                ...pagination,
                after: pageInfo.endCursor,
              },
            },
          })
        }
      >
        {t('groups.grouplist.loadmore')}
      </Button>
    </div>
  )

  const list = (
    <List className={s.list} split={false}>
      {events.map((event) => (
        <List.Item
          className={s.listItem}
          onClick={() => history.push(routes.posts.page(event.postId))}
          key={event.postId}
          extra={<EventActions data={event} hasDivider={false} iconOnly={true} />}
        >
          <List.Item.Meta title={event.topic} description={moment(event.startTime).format(format)} />
        </List.Item>
      ))}
      {loadMore}
    </List>
  )

  return (
    <div className={s.container}>
      {header}
      {loading ? loadingComponent : list}
    </div>
  )
}

export const MyFinishedEvent: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const pagination = {
    first: 3,
    sortBy: 'startTime',
  }

  const { data, loading, fetchMore } = useGetViewerFinishedEventsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      pagination,
    },
  })

  const events = data?.viewer.finishedEvents?.edges.map(({ node }) => node) || []

  const pageInfo = data?.viewer.finishedEvents?.pageInfo || {
    startCursor: null,
    endCursor: null,
    hasNextPage: null,
    hasPreviousPage: null,
  }

  const loadingComponent = (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Spin siz='large' delay={300}></Spin>
    </div>
  )

  const header = (
    <Typography.Title className={s.header} level={5} style={{ margin: '16px 0' }}>
      {t('viewer.event.end')}
    </Typography.Title>
  )

  const loadMore = pageInfo.hasNextPage && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
      <Button
        shape='round'
        onClick={() =>
          fetchMore({
            variables: {
              pagination: {
                ...pagination,
                after: pageInfo.endCursor,
              },
            },
          })
        }
      >
        {t('groups.grouplist.loadmore')}
      </Button>
    </div>
  )

  const list = (
    <List className={s.list} split={false}>
      {events.map((event) => (
        <List.Item
          onClick={() => history.push(routes.posts.page(event.postId))}
          className={s.listItem}
          key={event.postId}
        >
          <List.Item.Meta title={event.topic} />
        </List.Item>
      ))}
      {loadMore}
    </List>
  )

  return (
    <div className={s.container}>
      {header}
      {loading ? loadingComponent : list}
    </div>
  )
}
